'use client';
import { type ReactNode, useSyncExternalStore } from 'react';
import { CmsLink } from '@/components/CmsLink';
import { getObeBaseUrl, shouldUseBetaObe } from '@/lib/buildObeSearchUrl';
import type { CmsLink as CmsLinkData } from '@/services/cms-sdk';
import { urlForCmsLink } from '@/services/cms-sdk/cms/utils/urlForCmsLink';

export interface BookNowCtaLinkProps {
    className?: string;
    link: CmsLinkData;
    children?: ReactNode;
}

// The beta toggle only changes on a full navigation, so there is nothing to subscribe to.
const subscribe = () => () => {};

/** Repoint the CMS href onto the beta OBE origin, but only while the `?useBetaObe` toggle is active. */
function resolveObeHref(link: CmsLinkData): string {
    const href = urlForCmsLink(link);
    if (!shouldUseBetaObe()) {
        return href;
    }
    try {
        const url = new URL(href);
        const base = getObeBaseUrl();
        url.protocol = base.protocol;
        url.host = base.host;
        return url.href;
    } catch {
        // Relative/non-absolute links have no origin to swap; leave them untouched.
        return href;
    }
}

/**
 * Client wrapper around {@link CmsLink} for the "Book now" CTA. When the beta OBE toggle is active
 * (`?useBetaObe=1`), it swaps the OBE origin in the href for the beta one (JUCY-1513).
 *
 * The href is read via useSyncExternalStore so the server/hydration snapshot (default origin) matches
 * the SSR HTML, then React re-renders with the browser-only value once hydrated — no hydration mismatch.
 */
export function BookNowCtaLink({ className, link, children }: BookNowCtaLinkProps) {
    const href = useSyncExternalStore(
        subscribe,
        () => resolveObeHref(link),
        () => urlForCmsLink(link)
    );

    return (
        <CmsLink className={className} link={link} href={href}>
            {children}
        </CmsLink>
    );
}
